import { useEffect, useMemo, useRef } from "react"

import {
  useFetchBillingInfo,
  useFetchCurrentSubscription,
} from "src/data/billing/queries/billingQueries"
import {
  TFetchCurrentSubscriptionResponse,
  TUserBillingInfo,
} from "src/data/billing/types/billingTypes"
import {
  useFetchProducts,
  useFetchShippingDestinations,
} from "src/data/ecommerce/ecommerceQueries"
import { TCountryCode } from "src/data/ecommerce/ecommerceTypes"
import { useFetchParadiseUser } from "src/data/paradise/paradiseUsers/queries/paradiseUserQueries"
import { TParadiseUser } from "src/data/paradise/paradiseUsers/types/paradiseUserQueryTypes"

type TError = {
  title: string
  errorKey?: string
  message?: string
}

export function useParadiseCreateQuote({
  userId,
  shippingCountry,
  planId,
  onChangeUserSuccess,
}: {
  userId: string | null
  shippingCountry?: string
  planId?: string
  onChangeUserSuccess: (args: {
    user: TParadiseUser
    billingInfo?: TUserBillingInfo
    currentSubscription?: TFetchCurrentSubscriptionResponse
  }) => void
}) {
  const prevUserId = useRef<string | null>(null)

  const fetchShippingDestinations = useFetchShippingDestinations()

  const fetchParadiseUser = useFetchParadiseUser({
    userId: userId || "",
    options: {
      enabled: !!userId,
    },
  })

  const fetchUserBillingInfo = useFetchBillingInfo({
    userId: userId || "",
    options: {
      enabled: !!userId,
    },
  })

  const fetchUserCurrentSubscription = useFetchCurrentSubscription({
    userId: userId || "",
    props: {
      options: {
        enabled: !!userId,
      },
    },
  })

  const selectedDestination = fetchShippingDestinations.data?.find(
    (destination) => destination.country_code === shippingCountry
  )
  const fetchProducts = useFetchProducts({
    userId: userId || "",
    filter: {
      currency_code: selectedDestination?.currency_code,
      shipping_country: shippingCountry as TCountryCode,
    },
    options: {
      enabled: !!selectedDestination,
    },
  })

  const selectedProduct = fetchProducts.data?.products.find(
    (product) => product.plan_id === planId
  )
  const selectedPlanAssociatedShippableProducts =
    selectedProduct?.shippable_products.filter(
      (product) => product.type === "plan_associated"
    )
  const selectedAdditionalShippableProducts =
    selectedProduct?.shippable_products.filter(
      (product) => product.type === "additional"
    )

  useEffect(() => {
    if (!userId) {
      prevUserId.current = null
      return
    }

    if (
      !fetchUserBillingInfo.isLoading &&
      !fetchUserCurrentSubscription.isLoading &&
      fetchParadiseUser.data
    ) {
      if (prevUserId.current !== userId) {
        onChangeUserSuccess({
          user: fetchParadiseUser.data,
          billingInfo: fetchUserBillingInfo.data,
          currentSubscription: fetchUserCurrentSubscription.data || undefined,
        })
        prevUserId.current = userId
      }
    }
  }, [
    userId,
    onChangeUserSuccess,
    fetchUserBillingInfo,
    fetchUserCurrentSubscription,
    fetchParadiseUser,
  ])

  const fetchError: TError | null = useMemo(() => {
    if (fetchShippingDestinations.isError) {
      return {
        title: fetchShippingDestinations.error.message,
        message: "Failed to fetch shipping destinations",
      }
    }

    if (fetchParadiseUser.isError) {
      return {
        title: fetchParadiseUser.error.message,
        message: "Failed to fetch user",
      }
    }

    if (
      fetchUserBillingInfo.isError &&
      fetchUserBillingInfo.error.status !== 404
    ) {
      return {
        title: fetchUserBillingInfo.error.message,
        message: "Failed to fetch billing info",
      }
    }

    if (
      fetchUserCurrentSubscription.isError &&
      fetchUserCurrentSubscription.error.status !== 404
    ) {
      return {
        title: fetchUserCurrentSubscription.error.message,
        errorKey: fetchUserCurrentSubscription.error.response?.data?.error_key,
        message: fetchUserCurrentSubscription.error.response?.data?.message,
      }
    }

    if (fetchProducts.isError) {
      return {
        title: fetchProducts.error.message,
        errorKey: fetchProducts.error.response?.data?.errorKey,
        message:
          fetchProducts.error.response?.data?.message ||
          "Failed to fetch products",
      }
    }

    return null
  }, [
    fetchShippingDestinations,
    fetchParadiseUser,
    fetchUserBillingInfo,
    fetchUserCurrentSubscription,
    fetchProducts,
  ])

  return {
    shippingDestinations: fetchShippingDestinations.data,
    isLoadingShippingDestinations: fetchShippingDestinations.isLoading,
    products: fetchProducts.data?.products,
    isInitialLoadingProducts: fetchProducts.isInitialLoading,
    selectedProduct,
    selectedPlanAssociatedShippableProducts,
    selectedAdditionalShippableProducts,
    selectedUser: fetchParadiseUser.data,
    selectedUserCurrentSubscription: fetchUserCurrentSubscription.data,
    selectedUserBillingInfo: fetchUserBillingInfo.data,
    fetchError,
  }
}
